import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { computeSidemark } from '../utils';

const MARKUP = 1.30;
const FIRM_NAME = 'CIMON STARR';
const FIRM_ADDRESS = '3516 Sacramento Street, San Francisco, CA 94118';
const FIRM_EMAIL = 'purchasing@cimonstarr.com';
const FIRM_PHONE = '415-520-9020';

const PAYMENT_TERMS_OPTIONS = [
  '50% deposit, balance before shipment',
  '100% payment required',
  'Net 30',
  'Net 15',
  '30% deposit, 70% on delivery',
];

const STATUS_COLORS = {
  'Payment Received': { bg: '#e8f5e9', text: '#2e7d32' },
  'PO Verified': { bg: '#e8f5e9', text: '#2e7d32' },
  'Order Placed': { bg: '#ede7f6', text: '#4527a0' },
};

function fmt(n) {
  if (!n && n !== 0) return '$0';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const inp = {
  padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', outline: 'none',
  background: '#fff', boxSizing: 'border-box', width: '100%',
};

// Generate PO number
function genPONumber(vendorName, projectLabel) {
  const date = new Date();
  const yr = date.getFullYear().toString().slice(-2);
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const vendorCode = vendorName.replace(/[^a-zA-Z]/g, '').slice(0, 3).toUpperCase();
  const projCode = (projectLabel || '').replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
  return `PO-${yr}${mo}-${projCode}-${vendorCode}`;
}

export default function PurchaseOrderGenerator({ items, projects, vendors, onUpdateItem }) {
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'builder' | 'preview'
  const [poItems, setPoItems] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState(PAYMENT_TERMS_OPTIONS[0]);
  const [customTerms, setCustomTerms] = useState('');
  const [depositPct, setDepositPct] = useState(50);
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null); // saved PO record
  const [emailSent, setEmailSent] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState('');

  const parents = (items || []).filter(it => !it.parentId);

  // Items eligible for PO: Payment Received status, filtered by project
  const eligible = parents.filter(it =>
    it.status === 'Payment Received' &&
    (!selectedProject || it.projectLabel === selectedProject || it.project === selectedProject) &&
    (!selectedVendor || it.vendor === selectedVendor)
  );

  // Group eligible by vendor
  const byVendor = eligible.reduce((acc, it) => {
    const v = it.vendor || 'Unknown Vendor';
    if (!acc[v]) acc[v] = [];
    acc[v].push(it);
    return acc;
  }, {});

  // Get vendor details
  function getVendor(name) {
    return (vendors || []).find(v => v.name === name) || {};
  }

  // Get project details
  function getProject(label) {
    return (projects || []).find(p => p.label === label) || {};
  }

  function startPO(vendorName, vendorItems) {
    const proj = vendorItems[0]?.projectLabel || vendorItems[0]?.project || '';
    setSelectedVendor(vendorName);
    setPoItems(vendorItems.map(it => ({ ...it, selected: true, depositPct: depositPct })));
    setPoNumber(genPONumber(vendorName, proj));
    setView('builder');
  }

  function toggleItem(id) {
    setPoItems(prev => prev.map(it => it.id === id ? { ...it, selected: !it.selected } : it));
  }

  const selectedItems = poItems.filter(it => it.selected);
  const subtotal = selectedItems.reduce((s, it) => s + parseFloat(it.netPrice || it.unitCost || 0), 0);
  const depositAmount = subtotal * (depositPct / 100);
  const vendor = getVendor(selectedVendor);
  const projectLabel = selectedItems[0]?.projectLabel || selectedItems[0]?.project || '';
  const project = getProject(projectLabel);

  async function generatePO() {
    if (selectedItems.length === 0) { alert('Select at least one item.'); return; }
    setGenerating(true);

    // Save PO record to Supabase
    const poRecord = {
      po_number: poNumber,
      vendor_name: selectedVendor,
      project_label: projectLabel,
      item_ids: selectedItems.map(it => String(it.id)),
      subtotal,
      deposit_amount: depositAmount,
      deposit_pct: depositPct,
      payment_terms: customTerms || paymentTerms,
      notes,
      status: 'Generated',
      created_at: new Date().toISOString(),
    };

    await supabase.from('purchase_orders').insert(poRecord).catch(() => {});

    // Advance items to Order Placed
    selectedItems.forEach(it => {
      onUpdateItem(it.id, { ...it, status: 'Order Placed', poNumber, orderPlacedDate: new Date().toLocaleDateString() });
    });

    setGenerated(poRecord);
    setView('preview');
    setGenerating(false);
  }

  // Build PO content for PDF/email
  function buildPOContent() {
    const terms = customTerms || paymentTerms;
    const vendorAddr = [
      vendor.shipAttn ? `Attn: ${vendor.shipAttn}` : '',
      vendor.address || '',
      vendor.address2 || '',
      [vendor.city, vendor.state, vendor.zip].filter(Boolean).join(', '),
    ].filter(Boolean).join('\n');

    const shipAddr = [
      project.attn ? `Attn: ${project.attn}` : '',
      project.address || '',
      project.address2 || '',
      [project.city, project.state, project.zip].filter(Boolean).join(', '),
    ].filter(Boolean).join('\n');

    const lineItems = selectedItems.map((it, i) => {
      const cost = parseFloat(it.netPrice || it.unitCost || 0);
      const sidemark = computeSidemark(projectLabel, it.roomName, it.roomNumber);
      return {
        num: i + 1,
        item: it.productName || it.item,
        description: it.specs?.description || it.specs?.manufacturer || '',
        model: it.specs?.model || '',
        finish: it.specs?.finish || '',
        dimensions: it.specs?.dimensions || '',
        qty: it.qty || '1',
        qtyType: it.qtyType || 'Each',
        unitCost: cost,
        total: cost * parseFloat(it.qty || 1),
        leadTime: it.leadTime || it.specs?.leadTime || 'TBD',
        sidemark,
      };
    });

    return { poNumber, vendorName: selectedVendor, vendorAddr, shipAddr, lineItems, subtotal, depositPct, depositAmount, terms, notes, projectLabel, date: new Date().toLocaleDateString() };
  }

  async function previewPDF() {
    const po = buildPOContent();
    try {
      const response = await fetch('/api/generate-po-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(po),
      });
      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(URL.createObjectURL(blob));
      setPdfPreviewFilename(`${poNumber}.pdf`);
    } catch {
      // Fallback: open print dialog with HTML version
      const win = window.open('', '_blank');
      win.document.write(buildPOHTML(po));
      win.document.close();
      win.print();
    }
  }

  function buildPOHTML(po) {
    const rows = po.lineItems.map(li => `
      <tr>
        <td>${li.num}</td>
        <td><strong>${li.item}</strong>${li.description ? '<br><small>' + li.description + '</small>' : ''}${li.model ? '<br><small>Model: ' + li.model + '</small>' : ''}</td>
        <td>${li.finish || '—'}</td>
        <td>${li.dimensions || '—'}</td>
        <td>${li.qty} ${li.qtyType}</td>
        <td>${fmt(li.unitCost)}</td>
        <td><strong>${fmt(li.total)}</strong></td>
        <td>${li.leadTime}</td>
        <td style="font-size:10px;color:#6a1b9a">${li.sidemark}</td>
      </tr>`).join('');

    return `<!DOCTYPE html>
<html>
<head>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; padding: 40px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 20px; }
  .firm-name { font-size: 18px; font-weight: 700; letter-spacing: 2px; }
  .firm-info { font-size: 9px; color: #888; margin-top: 4px; }
  .po-title { font-size: 14px; font-weight: 700; text-align: right; }
  .po-number { font-size: 11px; color: #555; margin-top: 4px; }
  .addresses { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .addr-block h4 { font-size: 9px; font-weight: 700; letter-spacing: 1px; color: #888; text-transform: uppercase; margin-bottom: 6px; }
  .addr-block p { font-size: 11px; line-height: 1.5; white-space: pre-line; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  th { background: #1a1a1a; color: #fff; padding: 7px 8px; text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
  td { padding: 7px 8px; border-bottom: 1px solid #f0f0f0; vertical-align: top; font-size: 10px; }
  tr:nth-child(even) td { background: #fafafa; }
  .totals { text-align: right; margin-bottom: 20px; }
  .totals table { width: 280px; margin-left: auto; }
  .totals td { border: none; padding: 4px 8px; }
  .totals .total-row td { font-weight: 700; font-size: 12px; border-top: 1px solid #1a1a1a; }
  .terms { background: #f9f9f9; border: 1px solid #e0e0e0; padding: 12px 16px; border-radius: 4px; margin-bottom: 16px; }
  .terms h4 { font-size: 9px; font-weight: 700; letter-spacing: 1px; color: #888; text-transform: uppercase; margin-bottom: 6px; }
  .footer { border-top: 1px solid #e0e0e0; padding-top: 12px; font-size: 9px; color: #aaa; text-align: center; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="firm-name">${FIRM_NAME}</div>
      <div class="firm-info">${FIRM_ADDRESS}<br>${FIRM_EMAIL} · ${FIRM_PHONE}</div>
    </div>
    <div>
      <div class="po-title">PURCHASE ORDER</div>
      <div class="po-number">${po.poNumber}</div>
      <div class="po-number">Date: ${po.date}</div>
    </div>
  </div>

  <div class="addresses">
    <div class="addr-block">
      <h4>Vendor</h4>
      <p><strong>${po.vendorName}</strong>${po.vendorAddr ? '\n' + po.vendorAddr : ''}</p>
    </div>
    <div class="addr-block">
      <h4>Ship To</h4>
      <p>${po.shipAddr || po.projectLabel || 'See sidemark'}</p>
    </div>
    <div class="addr-block">
      <h4>Project</h4>
      <p><strong>${po.projectLabel}</strong></p>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>#</th><th>Item / Description</th><th>Finish</th><th>Dimensions</th>
        <th>Qty</th><th>Unit</th><th>Total</th><th>Lead Time</th><th>SIDEMARK</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="totals">
    <table>
      <tr><td>Subtotal</td><td><strong>${fmt(po.subtotal)}</strong></td></tr>
      <tr class="total-row"><td>Deposit Required (${po.depositPct}%)</td><td>${fmt(po.depositAmount)}</td></tr>
    </table>
  </div>

  <div class="terms">
    <h4>Payment Terms</h4>
    <p>${po.terms}</p>
    ${po.notes ? '<p style="margin-top:8px"><strong>Notes:</strong> ' + po.notes + '</p>' : ''}
  </div>

  <div class="footer">
    ${FIRM_NAME} · ${FIRM_ADDRESS} · ${FIRM_EMAIL} · ${FIRM_PHONE}
  </div>
</body>
</html>`;
  }

  function copyEmailText() {
    const po = buildPOContent();
    const lineList = po.lineItems.map(li =>
      `${li.num}. ${li.item}${li.model ? ' (' + li.model + ')' : ''}\n   Qty: ${li.qty} ${li.qtyType} · ${fmt(li.unitCost)} each · Lead: ${li.leadTime}\n   SIDEMARK: ${li.sidemark}`
    ).join('\n\n');

    const email = `Subject: [PO REQUEST] ${poNumber} — ${projectLabel}

To: purchasing@cimonstarr.com

Please find below our Purchase Order ${poNumber} for project ${projectLabel}.

VENDOR: ${selectedVendor}
PO NUMBER: ${poNumber}
DATE: ${po.date}

LINE ITEMS:

${lineList}

SUBTOTAL: ${fmt(subtotal)}
DEPOSIT REQUIRED (${depositPct}%): ${fmt(depositAmount)}

PAYMENT TERMS: ${customTerms || paymentTerms}

SHIP TO:
${po.shipAddr || projectLabel}

${notes ? 'NOTES:\n' + notes : ''}

Please feel free to reach out with any questions. Thank you,`;

    navigator.clipboard.writeText(email);
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 2500);
  }

  // ── PDF PREVIEW MODAL ─────────────────────────────────────────────────
  if (pdfPreviewUrl) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 6, overflow: 'hidden', width: '84vw', height: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5', gap: 8 }}>
            <span style={{ flex: 1, fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500 }}>Purchase Order Preview</span>
            <button style={{ height: 28, padding: '0 10px', borderRadius: 5, border: '0.5px solid #D8D7D2', background: '#FFFFFF', fontSize: 12, cursor: 'pointer' }} onClick={() => {
              const a = document.createElement('a');
              a.href = pdfPreviewUrl;
              a.download = pdfPreviewFilename;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }}>↓ Download</button>
            <button style={{ height: 28, padding: '0 10px', borderRadius: 5, border: '0.5px solid #D8D7D2', background: '#FFFFFF', fontSize: 12, cursor: 'pointer' }} onClick={() => {
              URL.revokeObjectURL(pdfPreviewUrl); setPdfPreviewUrl(null);
            }}>Close</button>
          </div>
          <iframe src={pdfPreviewUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="Purchase Order PDF" />
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────────────
  if (view === 'list') {
    const vendorNames = Object.keys(byVendor);
    return (
      <div style={{ maxWidth: 1000 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Purchase Orders</div>
          <div style={{ fontSize: 13, color: '#666' }}>
            Generate POs for items with Payment Received status. One PO per vendor.
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
            style={{ ...inp, width: 180 }}>
            <option value="">All Projects</option>
            {(projects || []).map(p => <option key={p.id} value={p.label}>{p.label}</option>)}
          </select>
        </div>

        {vendorNames.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#bbb', fontSize: 13, border: '1px dashed #ddd', borderRadius: 8 }}>
            No items with Payment Received status{selectedProject ? ` in ${selectedProject}` : ''}.<br />
            Items must be at Payment Received stage before a PO can be generated.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {vendorNames.map(vendorName => {
              const vItems = byVendor[vendorName];
              const total = vItems.reduce((s, it) => s + parseFloat(it.netPrice || it.unitCost || 0), 0);
              const v = getVendor(vendorName);
              return (
                <div key={vendorName} style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ background: '#f5f5f5', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{vendorName}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {vItems.length} item{vItems.length !== 1 ? 's' : ''} ·
                        {v.city && v.state ? ` ${v.city}, ${v.state} ·` : ''}
                        {v.email ? ` ${v.email}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(total)}</div>
                        <div style={{ fontSize: 10, color: '#888' }}>net cost</div>
                      </div>
                      <button onClick={() => startPO(vendorName, vItems)} style={{
                        padding: '8px 18px', background: '#1a1a1a', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}>
                        Generate PO →
                      </button>
                    </div>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                      {vItems.map((it, i) => (
                        <tr key={it.id} style={{ borderBottom: i < vItems.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                          <td style={{ padding: '8px 16px', fontWeight: 500 }}>{it.item}</td>
                          <td style={{ padding: '8px 16px', color: '#666' }}>{it.roomName || '—'}</td>
                          <td style={{ padding: '8px 16px', color: '#666' }}>{it.qty || '1'} {it.qtyType || ''}</td>
                          <td style={{ padding: '8px 16px', color: '#666', fontWeight: 600 }}>{fmt(parseFloat(it.netPrice || it.unitCost || 0))}</td>
                          <td style={{ padding: '8px 16px', color: '#888', fontSize: 10 }}>{it.sidemark || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── BUILDER VIEW ──────────────────────────────────────────────────────
  if (view === 'builder') {
    return (
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13 }}>← Back</button>
          <div style={{ fontWeight: 700, fontSize: 16 }}>PO for {selectedVendor}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>PO NUMBER</label>
            <input style={inp} value={poNumber} onChange={e => setPoNumber(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>DEPOSIT %</label>
            <input style={inp} type="number" min="0" max="100" value={depositPct}
              onChange={e => setDepositPct(Number(e.target.value))} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>PAYMENT TERMS</label>
            <select style={inp} value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)}>
              {PAYMENT_TERMS_OPTIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>CUSTOM TERMS (overrides above)</label>
            <input style={inp} placeholder="Leave blank to use selection above"
              value={customTerms} onChange={e => setCustomTerms(e.target.value)} />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>NOTES</label>
            <textarea style={{ ...inp, resize: 'vertical' }} rows={2} value={notes}
              onChange={e => setNotes(e.target.value)} placeholder="Special instructions, finishing notes..." />
          </div>
        </div>

        {/* Item selection */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', marginBottom: 8, letterSpacing: 0.5 }}>
            SELECT ITEMS — {selectedItems.length} of {poItems.length} selected
          </div>
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  {['', 'Item', 'Room', 'Qty', 'Net Cost', 'Lead Time', 'SIDEMARK'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {poItems.map((it, idx) => {
                  const cost = parseFloat(it.netPrice || it.unitCost || 0);
                  const sidemark = computeSidemark(projectLabel, it.roomName, it.roomNumber);
                  return (
                    <tr key={it.id} style={{ borderBottom: '1px solid #f5f5f5', background: it.selected ? '#f0f8ff' : idx % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                      onClick={() => toggleItem(it.id)}>
                      <td style={{ padding: '8px 10px' }}><input type="checkbox" checked={it.selected} onChange={() => toggleItem(it.id)} /></td>
                      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{it.item}</td>
                      <td style={{ padding: '8px 10px', color: '#666' }}>{it.roomName || '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#666' }}>{it.qty || '1'} {it.qtyType || ''}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{fmt(cost)}</td>
                      <td style={{ padding: '8px 10px', color: '#666' }}>{it.leadTime || it.specs?.leadTime || 'TBD'}</td>
                      <td style={{ padding: '8px 10px', color: '#9c6fc7', fontSize: 10 }}>{sidemark}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        {selectedItems.length > 0 && (
          <div style={{ background: '#f0f8ff', border: '1px solid #90caf9', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 32 }}>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>ITEMS</div><div style={{ fontSize: 18, fontWeight: 700 }}>{selectedItems.length}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>SUBTOTAL</div><div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(subtotal)}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>DEPOSIT ({depositPct}%)</div><div style={{ fontSize: 18, fontWeight: 700, color: '#0277bd' }}>{fmt(depositAmount)}</div></div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={generatePO} disabled={selectedItems.length === 0 || generating}
            style={{ padding: '9px 22px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: selectedItems.length > 0 ? 'pointer' : 'not-allowed', opacity: selectedItems.length > 0 ? 1 : 0.5 }}>
            {generating ? 'Generating...' : `Generate PO (${selectedItems.length} items)`}
          </button>
          <button onClick={() => setView('list')} style={{ padding: '9px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── PREVIEW VIEW ──────────────────────────────────────────────────────
  if (view === 'preview' && generated) {
    const po = buildPOContent();
    return (
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13 }}>← New PO</button>
          <div style={{ fontWeight: 700, fontSize: 16 }}>PO Generated — {generated.po_number}</div>
          <span style={{ background: '#e8f5e9', color: '#2e7d32', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10 }}>✓ Items advanced to Order Placed</span>
        </div>

        {/* PO Preview */}
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
          {/* Header */}
          <div style={{ background: '#1a1a1a', color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 2 }}>{FIRM_NAME}</div>
              <div style={{ fontSize: 10, color: '#aaa', marginTop: 3 }}>{FIRM_ADDRESS}</div>
              <div style={{ fontSize: 10, color: '#aaa' }}>{FIRM_EMAIL} · {FIRM_PHONE}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>PURCHASE ORDER</div>
              <div style={{ fontSize: 12, color: '#ccc', marginTop: 3 }}>{po.poNumber}</div>
              <div style={{ fontSize: 11, color: '#aaa' }}>{po.date}</div>
            </div>
          </div>

          {/* Addresses */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: '#e0e0e0' }}>
            {[
              { label: 'Vendor', content: <><strong>{po.vendorName}</strong>{po.vendorAddr ? <div style={{ whiteSpace: 'pre-line', marginTop: 4 }}>{po.vendorAddr}</div> : null}</> },
              { label: 'Ship To', content: <span style={{ whiteSpace: 'pre-line' }}>{po.shipAddr || po.projectLabel || 'See sidemark'}</span> },
              { label: 'Project', content: <strong>{po.projectLabel}</strong> },
            ].map(block => (
              <div key={block.label} style={{ background: '#fff', padding: '12px 16px' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#888', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>{block.label}</div>
                <div style={{ fontSize: 12 }}>{block.content}</div>
              </div>
            ))}
          </div>

          {/* Line items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['#', 'Item', 'Finish', 'Dimensions', 'Qty', 'Unit Cost', 'Total', 'Lead Time', 'SIDEMARK'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: '1px solid #e0e0e0', fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {po.lineItems.map((li, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f5f5f5', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '8px 10px', color: '#888' }}>{li.num}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{li.item}{li.model ? <div style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>Model: {li.model}</div> : null}</td>
                  <td style={{ padding: '8px 10px', color: '#666' }}>{li.finish || '—'}</td>
                  <td style={{ padding: '8px 10px', color: '#666' }}>{li.dimensions || '—'}</td>
                  <td style={{ padding: '8px 10px', color: '#666' }}>{li.qty} {li.qtyType}</td>
                  <td style={{ padding: '8px 10px', color: '#666' }}>{fmt(li.unitCost)}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 700 }}>{fmt(li.total)}</td>
                  <td style={{ padding: '8px 10px', color: '#666' }}>{li.leadTime}</td>
                  <td style={{ padding: '8px 10px', color: '#9c6fc7', fontSize: 10 }}>{li.sidemark}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals + terms */}
          <div style={{ padding: '16px 20px', background: '#fafafa', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ fontSize: 12, maxWidth: 400 }}>
              <div style={{ fontWeight: 700, fontSize: 10, color: '#888', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>Payment Terms</div>
              <div>{po.terms}</div>
              {po.notes && <div style={{ marginTop: 8, color: '#555' }}><strong>Notes:</strong> {po.notes}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Subtotal: <strong>{fmt(po.subtotal)}</strong></div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Deposit Due ({po.depositPct}%): {fmt(po.depositAmount)}</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={previewPDF} style={{ padding: '9px 20px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Preview PDF
          </button>
          <button onClick={copyEmailText} style={{ padding: '9px 20px', background: emailSent ? '#e8f5e9' : '#fff', color: emailSent ? '#2e7d32' : '#1a1a1a', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {emailSent ? '✓ Copied to clipboard' : '📋 Copy [PO REQUEST] Email'}
          </button>
          <button onClick={() => { setView('builder'); setGenerated(null); }} style={{ padding: '9px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
            Edit PO
          </button>
        </div>
      </div>
    );
  }

  return null;
}
